import React, { useState } from 'react';
import MetricCard from '../common/MetricCard';
import CyberBadge from '../common/CyberBadge';
import RazorpayCheckoutButton from '../common/RazorpayCheckoutButton';
import { 
  Wallet, 
  DollarSign, 
  ArrowUpRight, 
  CreditCard, 
  ShieldCheck, 
  Sparkles, 
  Copy, 
  Check, 
  Smartphone
} from 'lucide-react';

export default function FinanceView({ payments = [], bookings = [], stats }) {
  // Compute real total gross transaction volume
  const paidPayments = payments.filter((p) => p.status === 'paid' || p.status === 'completed');
  const paidBookings = bookings.filter((b) => b.paymentStatus === 'paid' || b.paymentStatus === 'completed');
  
  const paymentVolume = paidPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const bookingVolume = paidBookings.reduce((sum, b) => sum + (Number(b.totalAmount || b.amount) || 0), 0);
  const totalGrossNum = stats?.totalRevenue ?? (paymentVolume > 0 ? paymentVolume : bookingVolume);
  const grossRev = totalGrossNum.toLocaleString();
  const commission = Math.round(totalGrossNum * 0.05);
  const captainPayoutPool = totalGrossNum - commission;

  // Razorpay Checkout Interactive Test State
  const [checkoutAmount, setCheckoutAmount] = useState('100');
  const [checkoutItem, setCheckoutItem] = useState('VIP Party Pass');
  const [copiedField, setCopiedField] = useState(null);
  const [recentVerifiedPayments, setRecentVerifiedPayments] = useState([]);

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handlePaymentSuccess = (result) => {
    console.log('Payment checkout completed successfully:', result);
    const newTx = {
      id: `TXN-${(result.paymentId || '').substring(4, 10).toUpperCase()}`,
      item: `${checkoutItem} (Razorpay Web)`,
      amt: `₹${checkoutAmount}`,
      capt: `₹${Math.round(Number(checkoutAmount) * 0.95)}`,
      comm: `₹${Math.round(Number(checkoutAmount) * 0.05)}`,
      status: 'completed',
      paymentId: result.paymentId,
      orderId: result.orderId,
    };
    setRecentVerifiedPayments((prev) => [newTx, ...prev]);
  };

  // Build real transaction records from database payments and bookings
  const dbTransactions = payments.length > 0 ? payments.map((p) => {
    const amt = Number(p.amount) || 0;
    const comm = Math.round(amt * 0.05);
    const capt = amt - comm;
    return {
      id: p.gatewayOrderId || `TXN-${(p.id || p._id || '').slice(-6).toUpperCase()}`,
      paymentId: p.gatewayPaymentId,
      item: p.paymentFor ? p.paymentFor.toUpperCase() : (p.userName ? `Payment by ${p.userName}` : 'Platform Booking'),
      amt: `₹${amt.toLocaleString()}`,
      capt: `₹${capt.toLocaleString()}`,
      comm: `₹${comm.toLocaleString()}`,
      status: p.status || 'completed'
    };
  }) : bookings.map((b) => {
    const amt = Number(b.totalAmount || b.amount) || 0;
    const comm = Math.round(amt * 0.05);
    const capt = amt - comm;
    return {
      id: `TXN-${(b.id || b._id || '').slice(-6).toUpperCase()}`,
      paymentId: b.qrCode ? 'Verified Escrow Pass' : null,
      item: b.room?.title || (b.bookingType ? b.bookingType.toUpperCase() : 'Party Booking Pass'),
      amt: `₹${amt.toLocaleString()}`,
      capt: `₹${capt.toLocaleString()}`,
      comm: `₹${comm.toLocaleString()}`,
      status: b.paymentStatus || 'completed'
    };
  });

  const allTransactions = [
    ...recentVerifiedPayments,
    ...dbTransactions
  ];

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Wallet size={28} style={{ color: 'var(--neon-amber)' }} /> Financial Overview & Commission Ledger
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Platform gross transaction revenue, automated captain payout splits, and <strong style={{ color: 'var(--neon-amber)' }}>Dummy Gateway</strong> integration. Manage payments & subscriptions in the <strong style={{ color: 'var(--neon-cyan)' }}>Payments & Subscriptions</strong> tab.
        </p>
      </div>

      {/* Financial Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
        <MetricCard
          title="PLATFORM GROSS VOLUME"
          value={`₹${grossRev}`}
          subtext="Total transaction volume across nodes"
          change="+18.4%"
          changeType="up"
          icon={DollarSign}
          glowColor="amber"
        />
        <MetricCard
          title="5% PLATFORM COMMISSION"
          value={`₹${commission}`}
          subtext="Net platform earnings"
          change="Automated Cut"
          changeType="up"
          icon={ArrowUpRight}
          glowColor="emerald"
        />
        <MetricCard
          title="CAPTAIN PAYOUT POOL"
          value={`₹${captainPayoutPool.toLocaleString()}`}
          subtext="Processed to verified captains"
          change="Settled (95%)"
          changeType="neutral"
          icon={CreditCard}
          glowColor="cyan"
        />
      </div>

      {/* RAZORPAY STANDARD WEB CHECKOUT TEST CONSOLE */}
      <div 
        className="cyber-card" 
        style={{ 
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.04) 0%, rgba(15, 23, 42, 0.6) 100%)',
          borderColor: 'rgba(245, 158, 11, 0.35)',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(245, 158, 11, 0.08)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="badge badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 800 }}>
                <Sparkles size={14} /> LIVE GATEWAY
              </span>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0 }}>
                Razorpay Standard Web Checkout
              </h2>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '4px', margin: 0 }}>
              End-to-end checkout flow: Backend order creation, popup modal, HMAC-SHA256 signature verification, and automated ledger confirmation.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', padding: '4px 10px', background: 'rgba(16, 185, 129, 0.1)', color: '#10B981', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '6px', fontWeight: 700 }}>
              Backend: /api/create-order
            </span>
            <span style={{ fontSize: '0.75rem', padding: '4px 10px', background: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '6px', fontWeight: 700 }}>
              Backend: /api/verify-payment
            </span>
          </div>
        </div>

        {/* Credentials & Helper Hints */}
        <div 
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}
        >
          {/* Card Info */}
          <div 
            style={{ 
              background: 'rgba(0, 0, 0, 0.4)', 
              border: '1px solid rgba(255, 255, 255, 0.08)', 
              borderRadius: '8px', 
              padding: '14px 16px' 
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--neon-amber)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CreditCard size={14} /> Test Card Credentials
              </span>
              <button 
                type="button"
                onClick={() => copyToClipboard('4100280000001007', 'card')}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}
              >
                {copiedField === 'card' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                {copiedField === 'card' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', color: '#FFFFFF', letterSpacing: '0.05em' }}>
              4100 2800 0000 1007
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Expiry: <strong style={{ color: '#fff' }}>12/26</strong> · CVV: <strong style={{ color: '#fff' }}>123</strong>
            </div>
          </div>

          {/* UPI Info */}
          <div 
            style={{ 
              background: 'rgba(0, 0, 0, 0.4)', 
              border: '1px solid rgba(255, 255, 255, 0.08)', 
              borderRadius: '8px', 
              padding: '14px 16px' 
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--neon-cyan)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Smartphone size={14} /> Test UPI VPA
              </span>
              <button 
                type="button"
                onClick={() => copyToClipboard('test@razorpay', 'upi')}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}
              >
                {copiedField === 'upi' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                {copiedField === 'upi' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', color: '#FFFFFF', letterSpacing: '0.05em' }}>
              test@razorpay
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Select UPI tab inside modal and enter this VPA ID
            </div>
          </div>
        </div>

        {/* Amount Selection & Trigger */}
        <div 
          style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            gap: '16px',
            background: 'rgba(0, 0, 0, 0.25)',
            padding: '16px 20px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Item Name / Pass:
              </label>
              <input
                type="text"
                value={checkoutItem}
                onChange={(e) => setCheckoutItem(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-color)',
                  color: '#fff',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '0.85rem',
                  width: '180px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Amount (INR ₹):
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  min="1"
                  value={checkoutAmount}
                  onChange={(e) => setCheckoutAmount(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-color)',
                    color: '#fff',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '0.85rem',
                    width: '100px',
                    fontWeight: 700
                  }}
                />
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['1', '100', '499', '1200'].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setCheckoutAmount(amt)}
                      style={{
                        background: checkoutAmount === amt ? 'var(--neon-amber)' : 'rgba(255, 255, 255, 0.08)',
                        color: checkoutAmount === amt ? '#000' : 'var(--text-secondary)',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 8px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      ₹{amt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <RazorpayCheckoutButton
              amount={Number(checkoutAmount) || 100}
              currency="INR"
              itemName={checkoutItem}
              description="RAPMAP Web Standard Checkout"
              customerName="Admin Tester"
              customerEmail="test@razorpay.com"
              buttonText="Launch Razorpay Checkout"
              onSuccess={handlePaymentSuccess}
            />
          </div>
        </div>
      </div>

      {/* Transaction History Ledger */}
      <div className="cyber-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            Recent Platform Transaction Ledger
          </h3>
          <span className="badge badge-amber">
            <ShieldCheck size={12} /> SECURE GATEWAY
          </span>
        </div>

        <div className="table-container" style={{ border: 'none' }}>
          <table className="table">
            <thead>
              <tr>
                <th>TRANSACTION ID</th>
                <th>MODULE / ITEM</th>
                <th>TOTAL AMOUNT</th>
                <th>CAPTAIN SPLIT</th>
                <th>COMMISSION (5%)</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {allTransactions.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '36px', color: 'var(--text-muted)' }}>
                    No platform transactions recorded yet in the ledger. Completed bookings and payments will appear here automatically.
                  </td>
                </tr>
              ) : (
                allTransactions.map((tx, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--neon-cyan)' }}>
                      {tx.id}
                      {tx.paymentId && (
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                          {tx.paymentId}
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 700 }}>{tx.item}</td>
                    <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{tx.amt}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{tx.capt}</td>
                    <td style={{ fontWeight: 800, color: 'var(--neon-emerald)' }}>{tx.comm}</td>
                    <td><CyberBadge status={tx.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
